import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert, BellRing } from "lucide-react";
import { onboardingApi } from "../../services/onboardingApi";
import { RequirementChecklist } from "../../components/RequirementChecklist";
import { OnboardingProgress } from "../../components/OnboardingProgress";
import { StatusBadge } from "../../components/StatusBadge";
import { EagleState } from "../../components/EagleState";
import { useLocalization } from "../../hooks/useLocalization";
import { formatJst } from "../../lib/date";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { NotFoundState } from "../../components/NotFoundState";

export default function OnboardingCasePage() {
  const { caseId } = useParams();
  const { t } = useLocalization();
  const [onboardingCase, setOnboardingCase] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [clearanceResult, setClearanceResult] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [showOverride, setShowOverride] = useState(false);
  const [reminders, setReminders] = useState([]);

  const load = useCallback(() => {
    onboardingApi.getCase(caseId).then((r) => { setOnboardingCase(r.data); setNotFound(false); }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
    onboardingApi.getReminderHistory(caseId).then((r) => setReminders(r.data)).catch(() => {});
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  if (notFound) return <NotFoundState testId="onboarding-case-not-found" backTo="/management/onboarding" />;
  if (!onboardingCase) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  const handleUpdateStatus = async (requirementId, status) => {
    await onboardingApi.updateRequirement(requirementId, { status });
    load();
  };

  const handleEvaluate = async () => {
    const { data } = await onboardingApi.evaluateClearance(caseId);
    setClearanceResult(data);
    load();
  };

  const handleOverride = async () => {
    if (!overrideReason.trim()) return;
    await onboardingApi.overrideClearance(caseId, overrideReason.trim());
    setShowOverride(false);
    setOverrideReason("");
    load();
  };

  const handleManualReminder = async (requirementId) => {
    await onboardingApi.sendManualReminder(requirementId);
    load();
  };

  return (
    <div className="max-w-3xl" data-testid="onboarding-case-page">
      <PageHelpBanner pageKey="onboarding-case" title="How to use this page">
        <p>Update each requirement's status as documents come in, then click <strong>Evaluate Clearance</strong> - it checks every requirement automatically. Use <strong>Override</strong> only with a documented reason if someone must start work before every item clears.</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("onboarding.title")}</h2>
        <StatusBadge status={onboardingCase.status} />
      </div>

      {onboardingCase.status === "CLEARED_TO_WORK" ? (
        <div className="bg-white rounded-xl border border-emerald-200 mb-6">
          <EagleState variant="success" title={t("eagle.workClearanceGrantedTitle")} message={t("eagle.workClearanceGrantedMessage")} testId="case-clearance-granted" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <OnboardingProgress requirements={onboardingCase.requirements} />
        </div>
      )}

      <RequirementChecklist
        requirements={onboardingCase.requirements}
        canReview
        onUpdateStatus={handleUpdateStatus}
        onSendReminder={handleManualReminder}
      />

      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            data-testid="evaluate-clearance-button"
            onClick={handleEvaluate}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all"
          >
            <ShieldCheck size={16} /> {t("onboarding.evaluateClearance")}
          </button>
          {!showOverride && (
            <button
              data-testid="show-override-button"
              onClick={() => setShowOverride(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-amber-400 text-amber-600 text-sm font-semibold hover:bg-amber-50 active:scale-95 transition-all"
            >
              <ShieldAlert size={16} /> {t("onboarding.override")}
            </button>
          )}
        </div>

        {clearanceResult && (
          clearanceResult.cleared ? (
            <div data-testid="clearance-result">
              <EagleState variant="success" title={t("eagle.workClearanceGrantedTitle")} message={t("eagle.workClearanceGrantedMessage")} testId="clearance-eagle-success" />
            </div>
          ) : (
            <p className="mt-4 text-sm font-medium text-red-500" data-testid="clearance-result">{t("onboarding.blocked")}</p>
          )
        )}

        {showOverride && (
          <div className="mt-4 space-y-3">
            <textarea
              data-testid="override-reason-input"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder={t("onboarding.overrideReason")}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              rows={3}
            />
            <button
              data-testid="submit-override-button"
              onClick={handleOverride}
              disabled={!overrideReason.trim()}
              className="px-5 py-2 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 active:scale-95 transition-all disabled:opacity-40"
            >
              {t("common.submit")}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6" data-testid="reminder-history-section">
        <div className="flex items-center gap-2 mb-3">
          <BellRing size={16} className="text-blue-600" />
          <p className="text-sm font-semibold text-gray-800">{t("reminders.history")}</p>
        </div>
        {reminders.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="reminder-history-empty">{t("reminders.none")}</p>
        ) : (
          <div className="space-y-2">
            {reminders.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2" data-testid={`reminder-event-${r.id}`}>
                <span>{r.reminder_type} · {r.status}{r.suppression_reason ? ` (${r.suppression_reason})` : ""}</span>
                <span>{formatJst(r.timestamp_jst)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
